import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import {
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryDefinitionListResponse,
  SummaryViewKind,
  SummaryVisibility,
} from 'common/types/summaryDefinitions';

// Query Keys
export const SUMMARY_DEFINITIONS_QUERY_KEYS = {
  all: ['summaryDefinitions'] as const,
  global: () => [...SUMMARY_DEFINITIONS_QUERY_KEYS.all, 'global'] as const,
  firm: (firmId: number) => [...SUMMARY_DEFINITIONS_QUERY_KEYS.all, 'firm', firmId] as const,
  fund: (fundId: number) => [...SUMMARY_DEFINITIONS_QUERY_KEYS.all, 'fund', fundId] as const,
  valuations: (firmId: number) => [...SUMMARY_DEFINITIONS_QUERY_KEYS.all, 'valuations', firmId] as const,
  firmDefinition: (firmId: number, definitionId: number) =>
    [...SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId), definitionId] as const,
  fundDefinition: (fundId: number, definitionId: number) =>
    [...SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId), definitionId] as const,
};

// Hook Parameters
export interface UseGetFirmDefinitionsParams {
  firmId: number;
  enabled?: boolean;
}

export interface UseGetFundDefinitionsParams {
  fundId: number;
  enabled?: boolean;
}

export interface UseGetFirmValuationsDefinitionsParams {
  firmId: number;
  enabled?: boolean;
}

// Mutation Payloads
export interface CreateDefinitionPayload {
  name: string;
  // Optional: dashboard-kind definitions send no spreadsheet payload and take the model default.
  spreadsheet_definition?: SpreadsheetDefinition;
  view_kind?: SummaryViewKind;
  dashboard_definition?: DashboardDefinition;
  visibility?: SummaryVisibility;
  cloned_from?: number | null;
}

export interface UpdateDefinitionPayload {
  name?: string;
  spreadsheet_definition?: SpreadsheetDefinition;
  view_kind?: SummaryViewKind;
  dashboard_definition?: DashboardDefinition;
  visibility?: SummaryVisibility;
}

export interface UpdateOrderPayload {
  order: number[];
}

// Hook Return Types
export interface UseGetDefinitionsReturn {
  data: SummaryDefinitionListResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface UseMutationReturn<TData, TVariables> {
  mutate: (variables: TVariables) => void;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
}

// Computed definition list (merges global + custom in order)
export interface OrderedDefinition extends SummaryDefinition {
  isGlobal: boolean;
}

export type {
  DashboardDefinition,
  SummaryDefinition,
  SummaryDefinitionListResponse,
  SpreadsheetDefinition,
  SummaryViewKind,
  SummaryVisibility,
};
